/**
 * Welche Version läuft bei dir, welche ist aktuell? (29.09.2026, Ticket 2816)
 *
 * Martin, zur Karte „Browser-Erweiterung“: „Super, wenn ich hier sehen
 * würde, ob es ein Update gibt. … Auch die aktuelle Versionsnummer.“
 * Dasselbe für die Mac-App „Vespera Desktop“.
 *
 * EINE Stelle für beide Karten: der Vergleich und die Sätze, die in der
 * Karte stehen. Server (Status-Routen) und App (Einstellungen) nutzen sie.
 */

/**
 * „2.0.6“, „1.0.0“, „0.17.0“ — nur Ziffern und Punkte, höchstens vier Teile,
 * je Teil 1 bis 9 Ziffern.
 *
 * ⚠️ Genau so streng wie die Mac-App (update_logik.py, `[0-9]{1,9}` je Teil):
 * Was der Server großzügiger annähme, würde er ankündigen und die App
 * stillschweigend verwerfen. `[0-9]` statt `\d`, damit es dieselbe Regel
 * bleibt, auch wenn jemand sie in eine andere Sprache übersetzt.
 */
export function istVersion(v: unknown): v is string {
  return typeof v === "string" && v.length <= 20 && /^[0-9]{1,9}(\.[0-9]{1,9}){0,3}$/.test(v);
}

/**
 * Vergleicht Teil für Teil als Zahl: 1.10 ist neuer als 1.9, 2.0.10 neuer
 * als 2.0.9. Fehlende Teile zählen als 0 (2.0 = 2.0.0).
 */
export function vergleicheVersion(a: string, b: string): -1 | 0 | 1 {
  const ta = a.split(".").map(Number);
  const tb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(ta.length, tb.length); i++) {
    const x = ta[i] ?? 0;
    const y = tb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/**
 * Der Dateiname der Mac-App: „Vespera-Desktop-1.0.4.dmg“ — genau drei Zahlen
 * mit je 1 bis 9 Ziffern, nie ein Pfad. EINE Stelle für den Bau
 * (public/app-versionen.json), die Anzeige (server/app-versionen.ts), die
 * Signatur (scripts/desktop-signatur.mts) und die Update-Route
 * (server/app-update-route.ts): Wer hier großzügiger wäre als die App
 * (update_logik.py, `[0-9]{1,9}` je Teil), würde ein Update ankündigen, das
 * sie still verwirft.
 */
export const DESKTOP_DATEI = /^Vespera-Desktop-([0-9]{1,9})\.([0-9]{1,9})\.([0-9]{1,9})\.dmg$/;

/** Die Version im Dateinamen („1.0.4“) — oder null, wenn der Name nicht passt. */
export function desktopDateiVersion(name: unknown): string | null {
  if (typeof name !== "string" || name.length > 64) return null;
  const t = DESKTOP_DATEI.exec(name);
  return t ? `${t[1]}.${t[2]}.${t[3]}` : null;
}

/** SHA-256 in der Schreibweise des Vertrags: 64 Kleinbuchstaben-Hexzeichen. */
export const DESKTOP_SHA256 = /^[0-9a-f]{64}$/;

/**
 * Was die App an Größe annimmt (update_logik.py, GROESSE_MIN und GROESSE_MAX):
 * 1 bis 500 MB. Außerhalb zeigt sie kein Update an — der Bau sagt es vorher.
 */
export const DESKTOP_GROESSE_MIN = 1024 * 1024;
export const DESKTOP_GROESSE_MAX = 500 * 1024 * 1024;

/**
 * Ein sauberes Prüfpaar der DMG: sha256 (klein, 64 Hex) UND Größe (ganze Zahl
 * über 0) zusammen — sonst null. EINE Stelle für den Bau
 * (scripts/app-versionen.mts) und das Lesen (server/app-versionen.ts): ein
 * halbes Paar gibt es nicht.
 */
export function desktopPruefpaar(sha256: unknown, groesse: unknown): { sha256: string; groesse: number } | null {
  if (typeof sha256 !== "string" || !DESKTOP_SHA256.test(sha256)) return null;
  if (typeof groesse !== "number" || !Number.isSafeInteger(groesse) || groesse <= 0) return null;
  return { sha256, groesse };
}

/**
 * Ab dieser Version meldet die Erweiterung ihre Nummer beim Verbinden
 * (`{type:"hello", version}`). Eine verbundene Erweiterung ohne Meldung ist
 * also älter — und damit sicher ein Update wert.
 */
export const ERWEITERUNG_MELDET_AB = "2.0.6";

export type UpdateArt = "aktuell" | "update" | "unbekannt" | "nicht-verbunden";

export interface UpdateStand {
  art: UpdateArt;
  /** Eine ruhige Zeile: „Bei dir: 2.0.5 · Aktuell: 2.0.6“. */
  zeile: string;
  /** Was zu tun ist, nur wenn es etwas zu tun gibt. */
  hinweis: string | null;
}

interface VersionsLage {
  verbunden: boolean;
  /** Was das Gerät gemeldet hat — null, wenn es nichts meldet. */
  bei: string | null;
  /** Was ausgeliefert wird — null, wenn unbekannt. */
  aktuell: string | null;
}

const aktuellTeil = (aktuell: string | null): string => (aktuell ? ` · Aktuell: ${aktuell}` : "");

// 08.10.2026 (Prüfung K2): nicht „in denselben Ordner entpacken“ — der Finder legt dann „… 2“ an, Chrome sieht einen
// neuen Ordner = eine neue Erweiterung (neue Kennung, die Kopplung ist weg). Die Dateien im BISHERIGEN Ordner ersetzen.
export const ERWEITERUNG_ANLEITUNG =
  "Update verfügbar: Erweiterung unten neu herunterladen, die Dateien im bisherigen Ordner damit ersetzen und in chrome://extensions auf „Neu laden“ tippen.";

/** Die Zeile für die Karte „Browser-Erweiterung“. */
export function erweiterungStand(l: VersionsLage): UpdateStand {
  const bei = istVersion(l.bei) ? l.bei : null;
  const aktuell = istVersion(l.aktuell) ? l.aktuell : null;
  if (!l.verbunden) {
    return { art: "nicht-verbunden", zeile: `Nicht verbunden – Version unbekannt${aktuellTeil(aktuell)}`, hinweis: null };
  }
  if (!bei) {
    // Verbunden, aber stumm: älter als die erste Version, die sich meldet.
    return {
      art: "update",
      zeile: `Bei dir: älter als ${ERWEITERUNG_MELDET_AB}${aktuellTeil(aktuell)}`,
      hinweis: ERWEITERUNG_ANLEITUNG,
    };
  }
  if (!aktuell) return { art: "unbekannt", zeile: `Bei dir: ${bei}`, hinweis: null };
  if (vergleicheVersion(bei, aktuell) < 0) {
    return { art: "update", zeile: `Bei dir: ${bei} · Aktuell: ${aktuell}`, hinweis: ERWEITERUNG_ANLEITUNG };
  }
  return { art: "aktuell", zeile: `Bei dir: ${bei} · Aktuell: ${aktuell} ✓`, hinweis: null };
}

/**
 * Ab dieser Version aktualisiert sich die Mac-App selbst (Menüleiste: „Update
 * verfügbar → Jetzt aktualisieren“). Davor gibt es keinen Selbst-Updater: Wer
 * Vespera Desktop 1.0.3 oder älter hat, installiert die neue App EINMAL von
 * Hand — auch dort erscheint der Knopf nie.
 */
export const DESKTOP_SELBST_UPDATE_AB = "1.0.4";

/**
 * Der Satz unter der Zeile — je nachdem, ob das Gerät sich selbst aktualisieren
 * kann (`selbst`) oder einmal von Hand muss. Einen Link auf die neue App gibt
 * es vorerst nur für das Betreiberkonto (geschützte Testversion,
 * server/app-versionen.ts desktopTestDownload); alle anderen lesen „in
 * Vorbereitung“.
 *
 * ⚠️ Beide Wege enden gleich: Solange die App nicht von Apple signiert ist,
 * hält macOS jede neue Version für eine andere App — Bildschirmaufnahme und
 * Bedienungshilfen müssen danach neu erlaubt werden. Das steht in BEIDEN
 * Sätzen, nicht nur im Kleingedruckten.
 */
function desktopNeu(download: boolean, selbst: boolean): string {
  if (!download) return "Die neue Mac-App „Vespera Desktop“ ist in Vorbereitung.";
  return selbst
    ? "Update verfügbar: In der Menüleiste auf „Update verfügbar → Jetzt aktualisieren“ klicken. Solange die App nicht von Apple signiert ist, danach die Freigaben (Bildschirmaufnahme, Bedienungshilfen) neu erlauben."
    : `Update verfügbar, einmal von Hand: Vespera Desktop vor ${DESKTOP_SELBST_UPDATE_AB} (1.0.3 und älter) kann sich noch nicht selbst aktualisieren. Die neue Mac-App „Vespera Desktop“ unten herunterladen und in „Programme“ ziehen. Danach Bildschirmaufnahme und Bedienungshilfen neu erlauben. Ab dann zeigt die App Updates selbst an.`;
}

/**
 * Die Version hinter dem Download-Knopf, wenn sie als Vergleich taugt: neuer als die freigegebene (oder die
 * freigegebene unbekannt) — sonst null. Der Knopf (downloadText in frontend/lib/desktop-verbinden.ts) nennt
 * alles „Testversion“, was nicht die freigegebene ist; eine ÄLTERE Datei im Ordner (Rückfall in
 * server/app-versionen.ts desktopDownload) ist aber kein Vergleich — dann entscheidet die freigegebene.
 */
function desktopTestversion(aktuell: string | null, downloadVersion: unknown): string | null {
  if (!istVersion(downloadVersion)) return null;
  return aktuell === null || vergleicheVersion(downloadVersion, aktuell) > 0 ? downloadVersion : null;
}

/**
 * Die Zeile, wenn eine Testversion zum Herunterladen bereitliegt (nur Konto 1). (07.10.2026)
 *
 * Martin sah „Bei dir: 1.0.8 · Aktuell: 1.0.4 ✓“ neben „Vespera Desktop 1.0.9 herunterladen (Testversion)“:
 * „Aktuell“ war die freigegebene Fassung (desktop/vespera-desktop-version.json), das ✓ kam, weil 1.0.8 ≥ 1.0.4.
 * Liegt eine Testversion bereit, ist SIE der Vergleich — die freigegebene entscheidet hier nichts und wird nicht
 * genannt. ⚠️ „Jetzt aktualisieren“ in der Menüleiste kennt nur die freigegebene Fassung: Für die Testversion
 * gibt es keinen Menü-Satz, nur den Knopf unten. Vor 1.0.4 bleibt der Satz „einmal von Hand“ — er zeigt
 * ebenfalls auf den Knopf.
 */
function desktopTestStand(verbunden: boolean, bei: string | null, test: string, selbst: boolean): UpdateStand {
  const neuere = `Testversion ${test} liegt bereit (nicht im automatischen Update, bitte von Hand installieren)`;
  if (!verbunden) {
    return { art: "nicht-verbunden", zeile: `Nicht verbunden – Version unbekannt · neueste Testversion: ${test}`, hinweis: null };
  }
  if (!bei) {
    return { art: "update", zeile: `Version nicht gemeldet (ältere App) · ${neuere}`, hinweis: desktopNeu(true, false) };
  }
  const vergleich = vergleicheVersion(bei, test);
  if (vergleich < 0) {
    return { art: "update", zeile: `Bei dir: ${bei} · ${neuere}`, hinweis: selbst ? null : desktopNeu(true, false) };
  }
  if (vergleich === 0) return { art: "aktuell", zeile: `Bei dir: ${bei} · neueste Testversion: ${test}`, hinweis: null };
  // Neuer als alles zum Herunterladen (eigener Bau): nichts zu vergleichen.
  return { art: "aktuell", zeile: `Bei dir: ${bei}`, hinweis: null };
}

/** Die Zeile für die Karte „Vespera Desktop (Mac)“. `downloadVersion`: die Version hinter dem Download-Knopf. */
export function desktopStand(l: VersionsLage & { download?: boolean; downloadVersion?: string | null }): UpdateStand {
  const bei = istVersion(l.bei) ? l.bei : null;
  const aktuell = istVersion(l.aktuell) ? l.aktuell : null;
  // Ohne gemeldete Version (alte PawBot-App, 1.0.0/1.0.1) gilt: kein Selbst-Updater.
  const selbst = bei !== null && vergleicheVersion(bei, DESKTOP_SELBST_UPDATE_AB) >= 0;
  const test = l.download === true ? desktopTestversion(aktuell, l.downloadVersion) : null;
  if (test) return desktopTestStand(l.verbunden, bei, test, selbst);
  const neu = desktopNeu(l.download === true, selbst);
  if (!l.verbunden) {
    return { art: "nicht-verbunden", zeile: `Nicht verbunden – Version unbekannt${aktuellTeil(aktuell)}`, hinweis: null };
  }
  if (!bei) {
    // Die alte PawBot-App (0.x) meldet keine Version — sie gilt als „Update
    // verfügbar: neue App“ (Vorgabe 29.09. abends). ⚠️ Die neue App muss
    // deshalb `app_version` im session_start schicken, sonst stünde sie
    // hier fälschlich als alt.
    return { art: "update", zeile: `Version nicht gemeldet (ältere App)${aktuellTeil(aktuell)}`, hinweis: neu };
  }
  if (vergleicheVersion(bei, "1.0.0") < 0 || (aktuell && vergleicheVersion(bei, aktuell) < 0)) {
    return { art: "update", zeile: `Bei dir: ${bei}${aktuellTeil(aktuell)}`, hinweis: neu };
  }
  if (!aktuell) return { art: "unbekannt", zeile: `Bei dir: ${bei}`, hinweis: null };
  // Neuer als die freigegebene Fassung: „Aktuell: 1.0.4 ✓“ neben 1.0.8 verwirrte (07.10.2026) — sie entscheidet nichts.
  if (vergleicheVersion(bei, aktuell) > 0) return { art: "aktuell", zeile: `Bei dir: ${bei}`, hinweis: null };
  return { art: "aktuell", zeile: `Bei dir: ${bei} · Aktuell: ${aktuell} ✓`, hinweis: null };
}
